import * as React from 'react';

import pusherInitializer from '../../../../modules/pusherInitializer';
import GradeOptions from './grade_options';
import ImportClassesWarningModal from './import_classes_warning_modal';
import { providerConfigLookup } from './providerHelpers'

import { requestPost } from '../../../../modules/request/index';
import { DataTable, DropdownInput, ButtonSpinner } from '../../../Shared/index';
import useModalAccessibility from '../../../Shared/hooks/useModalAccessibility'

interface ImportProviderClassroomsModalProps {
  alreadyImportedProvider?: string | null;
  classrooms: Array<any>;
  close: (event) => void;
  onSuccess: (event) => void;
  provider: 'Canvas' | 'Clever' | 'Google'
  user: any;
}

const headers = [
  {
    width: '454px',
    name: 'Class',
    attribute: 'name'
  }, {
    width: '160px',
    name: 'Grade',
    attribute: 'grade',
    rowSectionClassName: 'show-overflow'
  }, {
    width: '74px',
    name: 'Year',
    attribute: 'year'
  }, {
    width: '74px',
    name: 'Students',
    attribute: 'students'
  }
]

const ImportProviderClassroomsModal = ({alreadyImportedProvider, classrooms: initialClassrooms, close, onSuccess, provider, user}: ImportProviderClassroomsModalProps) => {
  const { modalRef, } = useModalAccessibility(close)

  const [classrooms, setClassrooms] = React.useState(initialClassrooms);
  const [timesSubmitted, setTimesSubmitted] = React.useState(0);
  const [waiting, setWaiting] = React.useState(false);
  const [showWarning, setShowWarning] = React.useState(false);

  const providerConfig = providerConfigLookup[provider]

  const gradeOptions = GradeOptions

  const footerButtonClass = () => {
    let buttonClass = 'quill-button contained green medium focus-on-light';
    const noClassroomsChecked = classrooms.every(classroom => !classroom.checked)
    if (noClassroomsChecked) {
      buttonClass += ' disabled';
    }
    return buttonClass;
  }

  const toggleRowCheck = (rowId) => {
    const updatedClassrooms = classrooms.map(classroom => {
      if (classroom.classroom_external_id === rowId) { classroom.checked = !classroom.checked }

      return classroom
    })

    setClassrooms(updatedClassrooms)
  }

  const checkAllRows = () => {
    const newClassrooms = classrooms.map(classroom => {
      classroom.checked = true
      return classroom
    })
    setClassrooms(newClassrooms)
  }

  const uncheckAllRows = () => {
    const newClassrooms = classrooms.map(classroom => {
      classroom.checked = false
      return classroom
    })
    setClassrooms(newClassrooms)
  }

  const performImport = () => {
    pusherInitializer(user.id, providerConfig.importClassroomsEventName, () => onSuccess('Classes imported'))

    setWaiting(true)

    const selectedClassrooms = classrooms.filter(classroom => classroom.checked)

    requestPost(providerConfig.importClassroomsPath, { selected_classrooms: selectedClassrooms })
  }

  const handleClickImportClasses = () => {
    const classroomsCheckedWithNoGrade = classrooms.filter(classroom => classroom.checked && !classroom.grade)

    if (classroomsCheckedWithNoGrade.length) {
      const newClassrooms = classrooms.map(c => {
        if (classroomsCheckedWithNoGrade.find(noGradeClassroom => noGradeClassroom.id === c.id)) {
          c.error = 'Select a grade for your class'
        }
        return c
      })

      setClassrooms(newClassrooms);
      setTimesSubmitted(prevTimes => prevTimes + 1);
      return
    }

    if (alreadyImportedProvider) {
      setShowWarning(true)
      return
    }

    performImport()
  }

  const closeWarning = () => setShowWarning(false)

  const handleGradeChange = (rowId: string, grade: HTMLInputElement) => {
    const updatedClassrooms = classrooms.map(classroom => {
      if (classroom.classroom_external_id === rowId) { classroom.grade = grade.value }

      return classroom
    })

    setClassrooms(updatedClassrooms)
  }

  const renderModalContent = () => {
    if (!classrooms.length) { return }

    const rows = classrooms.map(classroom => {
      const { name, username, classroom_external_id, year, studentCount, checked, grade, error } = classroom
      const gradeOption = GradeOptions.find(g => [grade, Number(grade)].includes(g.value))
      const gradeSelector = (
        <DropdownInput
          ariaLabel="Select grade"
          className="small bordered grade"
          error={error && !grade ? error : null}
          handleChange={(g) => handleGradeChange(classroom_external_id, g)}
          options={gradeOptions}
          placeholder="Select grade"
          timesSubmitted={timesSubmitted}
          value={gradeOption}
        />
      )

      return {
        id: classroom_external_id,
        name,
        username,
        checked,
        year,
        className: error && !grade ? 'error' : '',
        grade: checked ? gradeSelector : null,
        students: studentCount,
        key: classroom_external_id,
      }
    })

    return (
      <DataTable
        checkAllRows={checkAllRows}
        checkRow={toggleRowCheck}
        headers={headers}
        reportingFormat={true}
        rows={rows}
        showCheckboxes={true}
        uncheckAllRows={uncheckAllRows}
        uncheckRow={toggleRowCheck}
      />
    )
  }

  const renderImportButton = () => {
    if (waiting) {
      return (
        <button
          aria-label="Loading"
          className={footerButtonClass()}
          type="button"
        >
          <ButtonSpinner />
        </button>

      )
    } else {
      return (
        <button
          className={footerButtonClass()}
          onClick={handleClickImportClasses}
          type="button"
        >
          Import classes
        </button>
      )
    }
  }

  if (showWarning) {
    return (
      <ImportClassesWarningModal
        alreadyImportedProvider={alreadyImportedProvider}
        close={closeWarning}
        importClasses={performImport}
        providerTitle={providerConfig.title}
        waiting={waiting}
      />
    )
  }

  return (
    <div className="modal-container import-provider-classrooms-modal-container">
      <div className="modal-background" />
      <div
        aria-labelledby="importClassesFromProviderModal"
        aria-modal={true}
        className="import-provider-classrooms-modal quill-modal"
        ref={modalRef}
        role="dialog"
        tabIndex={-1}
      >

        <div className="import-provider-classrooms-modal-header">
          <h3 className="title" id="importClassesFromProviderModal">Import classes from {providerConfig.title}</h3>
        </div>

        <div className="import-provider-classrooms-modal-body modal-body">
          {renderModalContent()}
        </div>

        <div className="import-provider-classrooms-modal-footer">
          <div className="buttons">
            <button className="quill-button outlined green medium focus-on-light" onClick={close} type="button">Cancel</button>
            {renderImportButton()}
          </div>
        </div>

      </div>
    </div>
  )
}

export default ImportProviderClassroomsModal
